/**
 * Auth tokens for axios and scope parsing — sessionStorage only (not localStorage).
 * Cleared when the tab closes or on logout.
 */

const ACCESS_STORAGE_KEY = "halo:access-token";
const ID_STORAGE_KEY = "halo:id-token";
const REFRESH_STORAGE_KEY = "halo:refresh-token";
const AUTH_EMAIL_STORAGE_KEY = "halo:auth-email";
const IDP_MODE_STORAGE_KEY = "halo:idp-mode";
const IDP_ROLES_STORAGE_KEY = "halo:idp-roles";
const IDP_SCOPES_STORAGE_KEY = "halo:idp-scopes";

function readSession(key) {
  if (typeof window === "undefined") return null;
  try {
    return window.sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeSession(key, value) {
  if (typeof window === "undefined") return;
  try {
    if (value) {
      window.sessionStorage.setItem(key, value);
    } else {
      window.sessionStorage.removeItem(key);
    }
  } catch {
    // private mode / quota
  }
}

function readJsonList(key) {
  const raw = readSession(key);
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.map(String).filter(Boolean);
  } catch {
    return [];
  }
}

export function setCognitoAccessToken(token) {
  writeSession(ACCESS_STORAGE_KEY, token || null);
}

export function getCognitoAccessToken() {
  return readSession(ACCESS_STORAGE_KEY);
}

export function setCognitoIdToken(token) {
  writeSession(ID_STORAGE_KEY, token || null);
}

export function getCognitoIdToken() {
  return readSession(ID_STORAGE_KEY);
}

export function setRefreshToken(token) {
  writeSession(REFRESH_STORAGE_KEY, token || null);
}

export function getRefreshToken() {
  return readSession(REFRESH_STORAGE_KEY);
}

export function setAuthEmail(email) {
  writeSession(AUTH_EMAIL_STORAGE_KEY, email || null);
}

export function getAuthEmail() {
  return readSession(AUTH_EMAIL_STORAGE_KEY);
}

export function getStoredIdpMode() {
  return readSession(IDP_MODE_STORAGE_KEY);
}

export function setStoredIdpMode(mode) {
  writeSession(IDP_MODE_STORAGE_KEY, mode || null);
}

export function getIdpRoles() {
  return readJsonList(IDP_ROLES_STORAGE_KEY);
}

export function setIdpRoles(roles) {
  if (Array.isArray(roles)) {
    writeSession(IDP_ROLES_STORAGE_KEY, JSON.stringify(roles.map(String).filter(Boolean)));
  } else {
    writeSession(IDP_ROLES_STORAGE_KEY, null);
  }
}

export function getIdpScopes() {
  return readJsonList(IDP_SCOPES_STORAGE_KEY);
}

export function setIdpScopes(scopes) {
  if (Array.isArray(scopes)) {
    writeSession(IDP_SCOPES_STORAGE_KEY, JSON.stringify(scopes.map(String).filter(Boolean)));
  } else {
    writeSession(IDP_SCOPES_STORAGE_KEY, null);
  }
}

export function clearCognitoTokens() {
  writeSession(ACCESS_STORAGE_KEY, null);
  writeSession(ID_STORAGE_KEY, null);
  writeSession(REFRESH_STORAGE_KEY, null);
  writeSession(AUTH_EMAIL_STORAGE_KEY, null);
  writeSession(IDP_ROLES_STORAGE_KEY, null);
  writeSession(IDP_SCOPES_STORAGE_KEY, null);
}

/**
 * @param {{
 *   access_token?: string,
 *   id_token?: string,
 *   refresh_token?: string,
 *   roles?: string[],
 *   scopes?: string[],
 * }} tokens
 */
export function applyAuthTokens(tokens) {
  setCognitoAccessToken(tokens.access_token);
  if (tokens.id_token) {
    setCognitoIdToken(tokens.id_token);
  }
  if (tokens.refresh_token) {
    setRefreshToken(tokens.refresh_token);
  }
  if (Array.isArray(tokens.roles)) {
    setIdpRoles(tokens.roles);
    setIdpScopes(Array.isArray(tokens.scopes) ? tokens.scopes : []);
    setStoredIdpMode("entra");
    writeSession(ID_STORAGE_KEY, null);
    writeSession(REFRESH_STORAGE_KEY, null);
  } else if (tokens.id_token) {
    setIdpRoles(null);
    setIdpScopes(null);
    setStoredIdpMode("cognito");
  }
}

/** Seconds before expiry to treat access token as stale (proactive refresh). */
export const ACCESS_TOKEN_SKEW_SEC = 60;

export function decodeJwtPayload(jwt) {
  if (!jwt || typeof jwt !== "string") return null;
  const parts = jwt.split(".");
  if (parts.length < 2) return null;
  try {
    const base64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64 + "=".repeat((4 - (base64.length % 4)) % 4);
    return JSON.parse(atob(padded));
  } catch {
    return null;
  }
}

/**
 * @param {string | null | undefined} jwt — access token (Cognito or Entra)
 * @param {number} [skewSeconds] — refresh if exp is within this many seconds
 */
export function isAccessTokenUsable(jwt, skewSeconds = ACCESS_TOKEN_SKEW_SEC) {
  const payload = decodeJwtPayload(jwt);
  if (!payload) return false;
  if (payload.token_use && payload.token_use !== "access") return false;
  const exp = payload.exp;
  if (typeof exp !== "number") return false;
  const nowSec = Math.floor(Date.now() / 1000);
  return exp > nowSec + skewSeconds;
}
